import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { captureRefFromUrl } from "@/lib/refCookie";
import "@/App.css";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyPhone from "@/pages/VerifyPhone";
import Marketplace from "@/pages/Marketplace";
import PropertyDetail from "@/pages/PropertyDetail";
import JVPortal from "@/pages/JVPortal";
import JVDetail from "@/pages/JVDetail";
import JVSubmit from "@/pages/JVSubmit";
import HowItWorks from "@/pages/HowItWorks";
import ROICalculator from "@/pages/ROICalculator";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import CreateListing from "@/pages/CreateListing";
import BulkListings from "@/pages/BulkListings";
import Agents from "@/pages/Agents";
import AgentProfile from "@/pages/AgentProfile";
import Businesses from "@/pages/Businesses";
import BusinessDetail from "@/pages/BusinessDetail";
import CreateBusiness from "@/pages/CreateBusiness";
import NdaInbox from "@/pages/NdaInbox";
import Consultation from "@/pages/Consultation";
import Messages from "@/pages/Messages";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import HallOfFame from "@/pages/HallOfFame";
import AcceptInvite from "@/pages/AcceptInvite";
import Services from "@/pages/Services";
import Invest from "@/pages/Invest";
import Tourism from "@/pages/Tourism";
import GovernmentServices from "@/pages/GovernmentServices";
import Concierge from "@/pages/Concierge";
import ConciergeSuccess from "@/pages/ConciergeSuccess";
import TLTBPartnership from "@/pages/TLTBPartnership";
import AgentConsole from "@/pages/AgentConsole";
import ExplainJV from "@/pages/ExplainJV";
import JVCalculator from "@/pages/JVCalculator";
import AgentTraining from "@/pages/AgentTraining";
import AgentPayouts from "@/pages/AgentPayouts";
import AgencyDashboard from "@/pages/AgencyDashboard";
import AgencyPublicProfile from "@/pages/AgencyPublicProfile";
import AgencyAcceptInvite from "@/pages/AgencyAcceptInvite";
import CoListAcceptInvite from "@/pages/CoListAcceptInvite";
import PrivateListing from "@/pages/PrivateListing";

function ProtectedRoute({ children, role }) {
    const { user, loading } = useAuth();
    if (loading || user === null) {
        return (
            <div className="min-h-screen grid place-items-center">
                <div className="text-muted-foreground font-app">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (role && !user.roles?.includes(role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

function RefCookieCapture() {
    // On every route change, check for ?ref= in the URL and (if valid)
    // persist it to a 30-day cookie so the registration form auto-prefills.
    const location = useLocation();
    useEffect(() => {
        captureRefFromUrl();
    }, [location.search, location.pathname]);
    return null;
}

function AppRoutes() {
    return (
        <BrowserRouter>
            <RefCookieCapture />
            <Routes>
                <Route element={<Layout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify-phone" element={<ProtectedRoute><VerifyPhone /></ProtectedRoute>} />
                    <Route path="/marketplace" element={<Marketplace />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/invest" element={<Invest />} />
                    <Route path="/tourism" element={<Tourism />} />
                    <Route path="/government-services" element={<GovernmentServices />} />
                    <Route path="/concierge" element={<Concierge />} />
                    <Route path="/concierge/success" element={<ConciergeSuccess />} />
                    <Route path="/tltb-partnership" element={<TLTBPartnership />} />
                    <Route path="/agent-console" element={<ProtectedRoute><AgentConsole /></ProtectedRoute>} />
                    <Route path="/agents/explain-jv" element={<ExplainJV />} />
                    <Route path="/agents/jv-calculator" element={<JVCalculator />} />
                    <Route path="/agents/training" element={<AgentTraining />} />
                    <Route path="/agent/payouts" element={<ProtectedRoute><AgentPayouts /></ProtectedRoute>} />
                    <Route path="/agency" element={<ProtectedRoute><AgencyDashboard /></ProtectedRoute>} />
                    <Route path="/agency/invite/:token" element={<ProtectedRoute><AgencyAcceptInvite /></ProtectedRoute>} />
                    <Route path="/agency/:slug" element={<AgencyPublicProfile />} />
                    <Route path="/co-list/accept/:token" element={<ProtectedRoute><CoListAcceptInvite /></ProtectedRoute>} />
                    <Route path="/listings/private/:token" element={<PrivateListing />} />
                    <Route path="/listings/new" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
                    <Route path="/listings/bulk" element={<ProtectedRoute><BulkListings /></ProtectedRoute>} />
                    <Route path="/properties/:id" element={<PropertyDetail />} />
                    <Route path="/jv" element={<JVPortal />} />
                    <Route path="/jv/submit" element={<ProtectedRoute><JVSubmit /></ProtectedRoute>} />
                    <Route path="/jv/:id" element={<JVDetail />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/roi-calculator" element={<ROICalculator />} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/dashboard/listings/new" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
                    <Route path="/dashboard/listings/bulk" element={<ProtectedRoute><BulkListings /></ProtectedRoute>} />
                    <Route path="/agents" element={<Agents />} />
                    <Route path="/agents/:id" element={<AgentProfile />} />
                    <Route path="/businesses" element={<Businesses />} />
                    <Route path="/businesses/:id" element={<BusinessDetail />} />
                    <Route path="/dashboard/businesses/new" element={<ProtectedRoute><CreateBusiness /></ProtectedRoute>} />
                    <Route path="/dashboard/nda-inbox" element={<ProtectedRoute><NdaInbox /></ProtectedRoute>} />
                    <Route path="/consultation" element={<Consultation />} />
                    <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
                    <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                    <Route path="/hall-of-fame" element={<HallOfFame />} />
                    <Route path="/accept-invite/:token" element={<AcceptInvite />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <LangProvider>
                    <div className="App font-body bg-background text-foreground">
                        <AppRoutes />
                        <Toaster richColors position="top-right" />
                    </div>
                </LangProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}
